// Sidebar redesign — live badges on actionable nav groups.
// All hooks degrade gracefully — a failed fetch returns 0 and the badge
// hides (the sidebar suppresses zero counts). Polled at LIVE_BADGE_POLL_MS
// so they're directionally fresh without acting like real-time.
//
// Coordinated with backend: each hook uses a list endpoint that already
// supports a status filter (campaigns, sequences) or is intrinsically
// small (follow-ups, inbound calls). No new aggregate endpoints required.

import { useEffect, useState } from 'react';

import { campaignsAPI } from '../../services/campaignsAPI';
import { sequencesAPI } from '../../services/sequencesAPI';
import { followUpsAPI, inboundCallsAPI } from '../../services/api';

export const LIVE_BADGE_POLL_MS = 30_000;

interface UseLiveCountOpts {
  enabled?: boolean;
}

function useLiveCount(
  fetcher: () => Promise<number>,
  deps: ReadonlyArray<unknown>,
  opts: UseLiveCountOpts = {},
): number {
  const enabled = opts.enabled !== false;
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const n = await fetcher();
        if (!cancelled) setCount(Number.isFinite(n) ? n : 0);
      } catch {
        if (!cancelled) setCount(0);
      }
    };
    tick();
    const id = window.setInterval(tick, LIVE_BADGE_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  return count;
}

/** Pending (un-actioned) follow-ups — the canonical "due today" proxy. */
export function useFollowUpsPendingCount(enabled = true): number {
  return useLiveCount(
    async () => {
      const r = await followUpsAPI.getFollowUps();
      const items = r?.follow_ups ?? [];
      let pending = 0;
      for (const fu of items) {
        const s = (fu.follow_up_status || '').toLowerCase();
        if (!s || s === 'pending') pending += 1;
      }
      return pending;
    },
    [],
    { enabled },
  );
}

/** Inbound calls currently ringing / in-progress — pulses when ≥ 1. */
export function useInboundRingingCount(enabled = true): number {
  return useLiveCount(
    async () => {
      // The /inbound/calls endpoint accepts an optional status filter; the
      // backend treats unrecognised values as "no filter", so on a partial
      // mismatch we end up with the full recent list (worst case: a small
      // over-count on first paint, corrected within one poll).
      const calls = await inboundCallsAPI.getCalls(0, 50, undefined, undefined, 'ringing');
      if (!Array.isArray(calls)) return 0;
      const ringing = calls.filter((c) => {
        const s = String((c as { status?: unknown })?.status ?? '').toLowerCase();
        return s === 'ringing' || s === 'in_progress' || s === 'in-progress' || s === 'queued';
      });
      return ringing.length;
    },
    [],
    { enabled },
  );
}

/**
 * "Today" — composite queue badge. Spec: due-today follow-ups + scheduled
 * callbacks today + waiting inbound. Without a dedicated dashboard query
 * we approximate as `follow-ups pending + ringing inbound`. The two
 * underlying hooks already poll, so this is a derived value at zero
 * marginal cost.
 */
export function useTodayCount(opts: { enabled?: boolean } = {}): number {
  const followUps = useFollowUpsPendingCount(opts.enabled);
  const ringing = useInboundRingingCount(opts.enabled);
  return followUps + ringing;
}

export function useCampaignsPausedCount(enabled = true): number {
  return useLiveCount(
    async () => {
      const list = await campaignsAPI.list({ status: ['paused'] });
      return Array.isArray(list) ? list.length : 0;
    },
    [],
    { enabled },
  );
}

export function useSequencesDraftCount(enabled = true): number {
  return useLiveCount(
    async () => {
      const list = await sequencesAPI.list({ status: 'draft' });
      return Array.isArray(list) ? list.length : 0;
    },
    [],
    { enabled },
  );
}
